import { z } from 'zod';

/**
 * ONE CHECK-OUT TIME FOR MANY MISSED CHECK-OUTS (2026-10-21): a crew that left
 * through the camp gate, a tablet that died at four. The attendance day list
 * selects the people; the server finds each one's open check-in on that day,
 * and every check-out it writes is an approved MISSED_PUNCH_OUT correction —
 * the same record a per-person fix leaves, so the worker's history reads the
 * same either way.
 *
 * Two calls, one rule. `preview` names the open check-ins (and why a selected
 * person has none); the save names those check-ins back, so a visit closed in
 * between is reported, not closed twice. The time rule below is shared, so the
 * warning card and the save cannot disagree about who is skipped.
 */

/** The most check-ins one save may close. */
export const BULK_MISSED_CHECKOUT_CAP = 200;

/** No check-out may close a check-in this old — the server's `MAX_OPEN_INTERVAL_MS`. */
export const MISSED_CHECKOUT_MAX_OPEN_MS = 16 * 60 * 60_000;

/**
 * The shortest check-in → check-out pair a fix may write (ST112, owner
 * 2026-10-24): a 15:29 check-in closed at 15:30 is a one-minute pair the
 * rollup pays as 0 minutes, so the day stays INCOMPLETE — in the Missing
 * punches queue — while the save reported "Check-out set". Read by this
 * rule, the bulk save, and the one-off `close-unclosed-days` plan.
 */
export const MIN_PAIR_MINUTES = 30;

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const ids = z
  .array(z.string().min(1))
  .min(1)
  .max(BULK_MISSED_CHECKOUT_CAP)
  .refine((list) => new Set(list).size === list.length, 'Each id may appear once');

export const MissedCheckoutPreviewSchema = z.object({ date: ymd, employeeIds: ids });
export type MissedCheckoutPreviewInput = z.infer<typeof MissedCheckoutPreviewSchema>;

export const BulkMissedCheckoutSchema = z.object({
  date: ymd,
  /** Wall clock on `date`, in the company's zone. */
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM'),
  reason: z.string().trim().min(3).max(200),
  checkInIds: ids,
});
export type BulkMissedCheckoutInput = z.infer<typeof BulkMissedCheckoutSchema>;

/** Why a person or a check-in was left alone. */
export const MISSED_CHECKOUT_SKIPS = [
  'NOT_FOUND', // no such check-in, or a person outside the caller's scope
  'NOT_OPEN', // checked out, or checked in again, since the list was read
  'OTHER_DAY', // the check-in is not on the day being fixed
  'OWN_RECORD', // nobody fixes their own day
  'ALREADY_FIXED', // an earlier save's check-out for this check-in already stands at this time
  'BEFORE_CHECK_IN',
  'TOO_LONG',
  'IN_FUTURE',
  'TOO_SHORT', // under MIN_PAIR_MINUTES after the check-in: the rollup would pay it as nothing and the day would stay open
] as const;
export type MissedCheckoutSkip = (typeof MISSED_CHECKOUT_SKIPS)[number];

type Instant = string | Date;
const ms = (v: Instant): number => (typeof v === 'string' ? Date.parse(v) : v.getTime());

/**
 * Whether a check-out at `at` may close a check-in made at `checkedInAt`, or
 * the reason it may not. The one rule the warning card and the save both
 * apply — so a too-short pair is refused on the card, before it is saved. (A later check-in cannot be overtaken: the open one is the day's last,
 * and `at` is on the same day.)
 */
export function missedCheckoutTimeSkip(
  checkedInAt: Instant,
  at: Date,
  now: Date
): Extract<MissedCheckoutSkip, 'BEFORE_CHECK_IN' | 'TOO_LONG' | 'IN_FUTURE' | 'TOO_SHORT'> | null {
  const inAt = ms(checkedInAt);
  if (at.getTime() <= inAt) return 'BEFORE_CHECK_IN';
  if (at.getTime() - inAt >= MISSED_CHECKOUT_MAX_OPEN_MS) return 'TOO_LONG';
  if (at.getTime() > now.getTime()) return 'IN_FUTURE';
  if (at.getTime() - inAt < MIN_PAIR_MINUTES * 60_000) return 'TOO_SHORT';
  return null;
}

/* ===========================================================================
 * THE MISSING CHECK-OUT QUEUE — GET /corrections/missed-checkouts. Every past
 * day nobody closed, across all dates, newest first, one page at a time. The
 * day is "unclosed" by the server's one rule (`UNCLOSED_DAY_WHERE`); this only
 * pages it. `employeeId` narrows to one person inside the caller's reach;
 * `date` to one day — how a fix made from the queue asks whether its day left
 * it (owner, 2026-10-24: "why not tell the right thing?").
 * ======================================================================== */

export const MISSED_CHECKOUT_QUEUE_PAGE_SIZE = 50;

export const MissedCheckoutQueueQuerySchema = z.object({
  employeeId: z.string().min(1).optional(),
  date: ymd.optional(),
  /** The toolbar's search, over the whole queue: every word against the code and the names. */
  search: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(MISSED_CHECKOUT_QUEUE_PAGE_SIZE),
  /** The whole queue under the search as a file (merge E6): the rail badge's days. */
  format: z.enum(['excel', 'csv']).optional(),
});
export type MissedCheckoutQueueQuery = z.infer<typeof MissedCheckoutQueueQuerySchema>;
export type MissedCheckoutQueueQueryInput = z.input<typeof MissedCheckoutQueueQuerySchema>;
